// src/firebase.ts

import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getFirestore } from "firebase/firestore";
import { initializeAuth, browserLocalPersistence, browserPopupRedirectResolver, inMemoryPersistence, Auth } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getFunctions, Functions } from "firebase/functions";
import { getPerformance } from "firebase/performance";

/** 本番（eq-board）の Firebase config（env が無い場合のフォールバック） */
const prodFirebaseConfig = {
  apiKey: "AIzaSyBsp6z4ji-nmsaU6SiovItFWxeKzOzh_yw",
  authDomain: "eq-board.firebaseapp.com",
  projectId: "eq-board",
  storageBucket: "eq-board.firebasestorage.app",
  messagingSenderId: "1071908715727",
  appId: "1:1071908715727:web:4d50cb0984458c5f610bba",
  measurementId: "G-THBKCSQ8L6"
};

/** env から読み込んだ Firebase config（mode に対応する .env.<mode> から） */
const envFirebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// VITE_FIREBASE_PROJECT_ID が env に定義されていれば env 由来の config を使用。
// 無ければ本番（eq-board）にフォールバック。
// → development / staging mode は .env.development / .env.staging から Staging を向き、
//   production mode は .env.production から本番を向く。
const firebaseConfig = import.meta.env.VITE_FIREBASE_PROJECT_ID
  ? envFirebaseConfig
  : prodFirebaseConfig;

// 埋め込みモードの検出
const isEmbedMode = () => {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('embed') === 'true';
};

// iframe（/widget/embed-preview と、その中の /widget/rag）は管理画面と同じオリジンになる。
// 既定の永続化を使うと親のログイン保存領域を共有し、親のセッションを消す。
const isAuthIframe = () => {
  if (typeof window === 'undefined') return false;
  return window.self !== window.top;
};

const app = initializeApp(firebaseConfig);
const inAuthIframe = isAuthIframe();

// 埋め込みモードと iframe では Analytics / Performance を初期化しない。
export const analytics = !inAuthIframe && !isEmbedMode() ? getAnalytics(app) : null;

// Firestoreは常に初期化（データアクセスに必要）
export const db = getFirestore(app);

// Authは常に初期化（埋め込みモードでも参照先のnullを避ける）
// iframe は起動時からメモリ永続化にし、親の browserLocalPersistence を開かない。
export const auth: Auth = initializeAuth(app, {
  persistence: inAuthIframe ? inMemoryPersistence : browserLocalPersistence,
  // getAuth() はこれを自動で付ける。無いと signInWithPopup / getRedirectResult が argument-error になる。
  popupRedirectResolver: browserPopupRedirectResolver,
});

export const performance = inAuthIframe ? null : getPerformance(app);

export const storage = getStorage(app);

// Functions: メインアプリ（= env で指定したプロジェクト）の Functions を使用
export const functions: Functions = getFunctions(app, 'us-central1');
